import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  TabContent,
  TabPane,
  Spinner,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Link } from "react-router-dom"
import { getTopics, getTopicSlug } from "../../api/survey"
import { format } from "date-fns"

const ForumIndex = () => {
  const [selectedTopic, setSelectedTopic] = useState(null)
  const [topics, setTopics] = useState([])
  const [topicData, setTopicData] = useState(null)
  const [visibleTopics, setVisibleTopics] = useState(3)
  const [searchQuery, setSearchQuery] = useState("")
  const [filteredTopics, setFilteredTopics] = useState([])

  // New state for fallbacks
  const [loadingTopics, setLoadingTopics] = useState(false)
  const [loadingTopicData, setLoadingTopicData] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = "Forum | CA Portal"
    fetchTopics()
  }, [])

  useEffect(() => {
    if (selectedTopic) {
      fetchTopicData(selectedTopic)
    }
  }, [selectedTopic])

  const fetchTopics = async () => {
    setLoadingTopics(true)
    setError(null)
    try {
      const response = await getTopics()
      setTopics(response.data)
      setFilteredTopics(response.data)
      if (response.data.length > 0) {
        setSelectedTopic(response.data[0].slug)
      }
    } catch (err) {
      setError("Failed to load topics. Please try again later.")
    } finally {
      setLoadingTopics(false)
    }
  }

  const fetchTopicData = async slug => {
    setLoadingTopicData(true)
    setError(null)
    try {
      const response = await getTopicSlug(slug)
      setTopicData(response.data)
    } catch (err) {
      setError("Failed to load topic details. Please try again later.")
    } finally {
      setLoadingTopicData(false)
    }
  }

  const formatTimestamp = timestampISO => {
    const date = new Date(timestampISO)
    return format(date, "EEE dd MMMM 'at' HH:mm")
  }

  const renderPostCount = postcount => {
    return postcount === 1 ? "1 post" : `${postcount} posts`
  }

  const handleItemClick = slug => {
    setSelectedTopic(slug)
  }

  const handleSearchChange = e => {
    const query = e.target.value.toLowerCase()
    setSearchQuery(query)
    const filtered = topics.filter(topic =>
      topic.title.toLowerCase().includes(query)
    )
    setFilteredTopics(filtered)
  }

  return (
    <div className="page-content">
      <Breadcrumbs title="Forum" breadcrumbItem="Forum Content" />
      <Container fluid>
        <Row>
          {/* Sidebar topics */}
          <Col xl={3} lg={4}>
            <Card>
              <CardBody className="p-4">
                <div className="search-box">
                  <p className="text-muted">Search a Topic</p>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded bg-light border-light"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <i className="mdi mdi-magnify search-icon"></i>
                  </div>
                </div>
                <hr className="my-4 hr-dark" />

                <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                  {loadingTopics ? (
                    <div className="text-center my-3">
                      <Spinner color="primary" />
                      <p className="text-muted mt-2">Loading topics...</p>
                    </div>
                  ) : error ? (
                    <p className="text-danger">{error}</p>
                  ) : filteredTopics.length === 0 ? (
                    <p className="text-muted">No topics found.</p>
                  ) : (
                    <ul className="list-unstyled fw-medium">
                      {filteredTopics.slice(0, visibleTopics).map(topic => (
                        <li key={topic.cid}>
                          <Link
                            to="#"
                            onClick={() => handleItemClick(topic.slug)}
                            className={`text-primary ${
                              selectedTopic === topic.slug ? "fw-bold" : ""
                            }`}
                          >
                            <p
                              className="text-primary"
                              style={{ fontSize: "larger" }}
                            >
                              {topic.title}
                            </p>
                          </Link>
                          <p style={{ fontSize: "smaller" }}>
                            {formatTimestamp(topic.timestampISO)}
                          </p>
                          <p style={{ fontSize: "smaller" }}>
                            {renderPostCount(topic.postcount)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Topic content */}
          <Col xl={9} lg={8}>
            {loadingTopicData ? (
              <div className="text-center my-5">
                <Spinner color="primary" />
                <p className="text-muted mt-2">Loading topic details...</p>
              </div>
            ) : error ? (
              <p className="text-danger">{error}</p>
            ) : topicData ? (
              <Card>
                <TabContent className="p-4" activeTab="1">
                  <TabPane tabId="1">
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h3>{topicData.title}</h3>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p className="text-muted">
                            {renderPostCount(topicData.postcount)}
                          </p>
                          <p className="text-muted">
                            {topicData.locked === 1 ? "Locked" : "Open"}
                          </p>
                        </div>
                      </div>

                      {topicData.posts && topicData.posts.length > 0 ? (
                        topicData.posts.map((post, index) => (
                          <div key={index}>
                            <p
                              dangerouslySetInnerHTML={{ __html: post.content }}
                            ></p>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <p
                                className="text-muted"
                                style={{ fontSize: "smaller" }}
                              >
                                Posted By: {post.user.username}
                              </p>
                              <p
                                className="text-muted"
                                style={{ fontSize: "smaller" }}
                              >
                                {formatTimestamp(post.timestamp)}
                              </p>
                            </div>
                            <hr />
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No posts available.</p>
                      )}
                    </div>
                  </TabPane>
                </TabContent>
              </Card>
            ) : (
              <p className="text-muted">Select a topic to view details.</p>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ForumIndex
